<template>
  <div class="studio-panel">
    <h2>Syndication</h2>
    <p class="studio-panel-subtitle">Embed your drops on external sites and partner platforms</p>

    <div v-if="loading" class="studio-loading">
      <span class="studio-loading-dot"></span>
      <span class="studio-loading-text">Loading syndication settings...</span>
    </div>

    <div v-if="!loading && dropGroup" class="syndication-content">
      <!-- Embed Code -->
      <div class="studio-card" style="margin-bottom: 20px;">
        <h3 style="margin-top: 0;">Embed Code</h3>
        <p class="helper-text">Paste this where the widget should appear on any website.</p>
        <div class="embed-kind" role="radiogroup" aria-label="Widget">
          <label><input type="radio" value="drop" v-model="embedKind" /> Daily drop</label>
          <label><input type="radio" value="horoscope" v-model="embedKind" /> Horoscope by sign</label>
        </div>
        <div class="embed-code-wrap">
          <code class="embed-code">{{ embedCode }}</code>
          <button class="copy-btn" @click="copyEmbed">{{ copied ? 'Copied!' : 'Copy' }}</button>
        </div>
      </div>

      <!-- Widget Theme -->
      <div class="studio-card" style="margin-bottom: 20px;">
        <h3 style="margin-top: 0;">Widget Theme</h3>

        <div class="studio-form-group">
          <label>Background Color</label>
          <input class="studio-input" v-model="theme.widget_background_color" type="text" placeholder="#1a0a2e" />
        </div>
        <div class="studio-form-group">
          <label>Border Color</label>
          <input class="studio-input" v-model="theme.widget_border_color" type="text" placeholder="#663399" />
        </div>
        <div class="studio-form-group">
          <label>Title Color</label>
          <input class="studio-input" v-model="theme.widget_title_color" type="text" placeholder="#FFDFA3" />
        </div>
        <div class="studio-form-group">
          <label>Title Font Family</label>
          <input class="studio-input" v-model="theme.widget_title_font_family" type="text" placeholder="Cinzel, serif" />
        </div>

        <div class="studio-form-actions" style="border-top: none; padding-top: 0; margin-top: 12px;">
          <button class="studio-btn-primary" @click="saveTheme" :disabled="saving">
            {{ saving ? 'Saving...' : 'Save Theme' }}
          </button>
          <span v-if="saveSuccess" class="studio-success">Saved!</span>
        </div>
      </div>

      <!-- Labels -->
      <div class="studio-card">
        <h3 style="margin-top: 0;">Custom Labels</h3>

        <div class="studio-form-group">
          <label>Horoscope Alias</label>
          <input class="studio-input" v-model="theme.drop_horoscope_alias" type="text" placeholder="Daily Mystic Drop" />
        </div>
        <div class="studio-form-group">
          <label>Share Label</label>
          <input class="studio-input" v-model="theme.share_label_alias" type="text" placeholder="Share this reading" />
        </div>
      </div>
    </div>

    <div v-if="!loading && !dropGroup" class="studio-stub">
      <span class="studio-stub-icon">✦</span>
      <p>No syndication group configured yet. Create a drop group to enable embedding.</p>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import axios from 'axios'
import { store, static_store } from '@/store'
import { parseAxiosError } from '@/utils/general-utils'
import { trackShare } from '@/utils/analytics'
import { embedSnippet } from '@/utils/embed-code'

const loading = ref(true)
const saving = ref(false)
const saveSuccess = ref(false)
const copied = ref(false)
const dropGroup = ref(null)

const theme = reactive({
  widget_background_color: '',
  widget_border_color: '',
  widget_title_color: '',
  widget_title_font_family: '',
  drop_horoscope_alias: '',
  share_label_alias: '',
})

function authHeaders() {
  const token = localStorage.getItem('authToken')
  return token ? { Authorization: `Token ${token}` } : {}
}

const embedKind = ref('drop')
const embedCode = computed(() => embedSnippet(store.reader?.sub_domain, embedKind.value))

function copyEmbed() {
  navigator.clipboard.writeText(embedCode.value)
  trackShare('embed_code', 'syndication', store.reader?.sub_domain, { widget_type: embedKind.value })
  copied.value = true
  setTimeout(() => { copied.value = false }, 2000)
}

async function saveTheme() {
  if (!dropGroup.value) return
  saving.value = true
  saveSuccess.value = false
  try {
    await axios.patch(
      `${static_store.apiUrl}/reader/update-drop-setting/${dropGroup.value.id}/`,
      { ...theme },
      { headers: authHeaders() }
    )
    saveSuccess.value = true
    setTimeout(() => { saveSuccess.value = false }, 3000)
  } catch (error) {
    console.error('Save theme failed:', parseAxiosError(error))
  } finally {
    saving.value = false
  }
}

onMounted(async () => {
  try {
    const res = await axios.get(`${static_store.apiUrl}/reader/my-drop-settings/`, { headers: authHeaders() })
    const groups = res.data.groups || []
    if (groups.length > 0) {
      dropGroup.value = groups[0]
      Object.keys(theme).forEach(k => {
        if (groups[0][k]) theme[k] = groups[0][k]
      })
    }
  } catch {
    // silent
  } finally {
    loading.value = false
  }
})
</script>

<style scoped>
@import '@/styles/studio-forms.css';

.helper-text { font-size: 12px; color: rgba(195,169,255,0.4); font-style: italic; margin: 0 0 12px; }
.embed-kind { display: flex; gap: 16px; margin: 0 0 10px; font-size: 13px; color: rgba(255,255,255,0.75); }
.embed-kind label { display: inline-flex; align-items: center; gap: 6px; cursor: pointer; }
.embed-code-wrap { display: flex; gap: 8px; align-items: flex-start; }
.embed-code { flex: 1; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.1); border-radius: 6px; padding: 10px; font-size: 11px; color: #C9A94E; word-break: break-all; line-height: 1.5; }
.copy-btn { padding: 8px 16px; background: rgba(201,169,78,0.15); border: 1px solid rgba(201,169,78,0.3); border-radius: 6px; color: #C9A94E; font-size: 12px; cursor: pointer; font-family: inherit; white-space: nowrap; transition: background 0.2s; }
.copy-btn:hover { background: rgba(201,169,78,0.25); }
</style>
